import app from 'flarum/forum/app';
import UserPage from 'flarum/forum/components/UserPage';
import Button from 'flarum/common/components/Button';
import Link from 'flarum/common/components/Link';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Placeholder from 'flarum/common/components/Placeholder';
import avatar from 'flarum/common/helpers/avatar';
import username from 'flarum/common/helpers/username';

/**
 * "Blocked users" tab on the current user's own profile, shown next to
 * Posts / Discussions / Likes. Lists everyone the user blocked with an
 * unblock button per row.
 *
 * The list comes from FoF Ignore's `ignoredUsers` relationship, which is
 * preloaded in the forum payload and refreshed here from the API.
 */
export default class BlockedUsersPage extends UserPage {
  oninit(vnode) {
    super.oninit(vnode);

    this.loading = true;
    this.unblockingId = null;
    this.blockedUsers = [];

    if (!app.session.user) {
      m.route.set('/');
      return;
    }

    this.loadUser(app.session.user.username());

    // Re-fetch the current user so the list also reflects blocks made
    // after the page was first loaded (other tabs, other devices).
    app.store
      .find('users', app.session.user.id())
      .catch(() => {})
      .then(() => {
        this.blockedUsers = app.session.user.ignoredUsers() || [];
        this.loading = false;
        m.redraw();
      });
  }

  show() {
    this.user = app.session.user;
    m.redraw();
  }

  content() {
    if (this.loading) {
      return (
        <div className="GhostBlock-list">
          <LoadingIndicator />
        </div>
      );
    }

    return (
      <div className="GhostBlock-list">
        {this.limitSummary()}
        {this.blockedUsers.length ? (
          <ul className="GhostBlock-listItems">
            {this.blockedUsers.map((user) => (
              <li className="GhostBlock-listItem" key={user.id()}>
                <Link href={app.route.user(user)} className="GhostBlock-listUser">
                  {avatar(user)} {username(user)}
                </Link>
                {/* A moderator separation keeps them hidden regardless — don't offer an unblock. */}
                {!user.ghostSeparated() && (
                <Button
                  className="Button GhostBlock-listUnblock"
                  icon="fas fa-user-check"
                  loading={this.unblockingId === user.id()}
                  onclick={() => this.unblock(user)}
                >
                  {app.translator.trans('happydigital-ghost-block.forum.profile.unblock_button')}
                </Button>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <Placeholder text={app.translator.trans('happydigital-ghost-block.forum.blocked_list.empty')} />
        )}
      </div>
    );
  }

  limitSummary() {
    const actor = app.session.user;
    const limit = actor?.ghostBlockLimit?.();

    if (limit === undefined || limit === null) return null;

    const used = actor.ghostBlockUsed();

    return (
      <p className="GhostBlock-limitSummary">
        {app.translator.trans('happydigital-ghost-block.forum.blocked_list.limit_summary', { used, limit })}
      </p>
    );
  }

  unblock(user) {
    this.unblockingId = user.id();

    // FoF Ignore's backend listener removes the pivot row.
    user
      .save({ ignored: false })
      .then(() => {
        this.blockedUsers = this.blockedUsers.filter((u) => u.id() !== user.id());
      })
      .catch(() => {})
      .then(() => {
        this.unblockingId = null;
        m.redraw();
      });
  }
}
